import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProgressByStudentId } from "../../services/ProgressService";
import { getStudiesByStudentId } from "../../services/StudyService";
import Chart from "react-apexcharts";

const ProgressBar = ({ percentage, color }) => {
  const normalizedPercentage = Math.max(0, Math.min(100, percentage));

  return (
    <div className="progress mt-3" style={{ height: "10px" }}>
      <div
        className="progress-bar"
        role="progressbar"
        style={{ width: `${normalizedPercentage}%`, backgroundColor: color }}
        aria-valuenow={normalizedPercentage}
        aria-valuemin="0"
        aria-valuemax="100"
      ></div>
    </div>
  );
};

const ProgressStatus = ({ status, count, iconClass, colorClass }) => {
  const badgeClasses = `text-white px-3 py-1 rounded-3 ms-2 d-inline-flex align-items-center ${colorClass}`;

  return (
    <h5 className="text-center mt-3">
      {status} progress
      <span className={badgeClasses} style={{ fontSize: "0.9em" }}>
        <i className={`${iconClass} me-2`}></i>
        {count}
      </span>
    </h5>
  );
};

export const OveralProgress = () => {
  const { id } = useParams();
  const [progress, setProgress] = useState([]);
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    const fetchProgressAndStudies = async () => {
      try {
        const progressRes = await getProgressByStudentId(id);
        setProgress(progressRes.data);

        const studiesRes = await getStudiesByStudentId(id);
        setStudies(studiesRes.data);
      } catch (error) {
        console.error("Failed to fetch progress or studies data", error);
      }
    };
    fetchProgressAndStudies();
  }, [id]);

  const getLatestFinishDate = () => {
    if (studies.length === 0) return null;
    const latestFinishDate = studies.reduce((latest, study) => {
      const finishDate = new Date(study.finishDate);
      return finishDate > latest ? finishDate : latest;
    }, new Date(0));
    return latestFinishDate;
  };

  const latestFinishDate = getLatestFinishDate();

  const evaluateProgress = () => {
    let goodCount = 0;
    let moderateCount = 0;
    let poorCount = 0;

    progress.forEach((item) => {
      const submitThesisDate = new Date(item.submitThesis);
      const studyCompletionDate = new Date(item.studyComplition);
      const meetProgressPlanYes = item.meetProgressPlan === "YES";

      if (
        isNaN(submitThesisDate.getTime()) ||
        isNaN(studyCompletionDate.getTime())
      ) {
        console.error("Invalid date for progress record:", item);
        return;
      }

      if (latestFinishDate) {
        const submitThesisBeforeFinishDate =
          submitThesisDate <= latestFinishDate;
        const studyCompletionBeforeFinishDate =
          studyCompletionDate <= latestFinishDate;

        let criteriaMetCount = 0;
        if (meetProgressPlanYes) criteriaMetCount++;
        if (submitThesisBeforeFinishDate) criteriaMetCount++;
        if (studyCompletionBeforeFinishDate) criteriaMetCount++;

        if (criteriaMetCount === 3) {
          goodCount++;
        } else if (criteriaMetCount === 2) {
          moderateCount++;
        } else {
          poorCount++;
        }
      }
    });

    const total = progress.length;

    const goodPercentage =
      total > 0 ? ((goodCount / total) * 100).toFixed(2) : 0;
    const moderatePercentage =
      total > 0 ? ((moderateCount / total) * 100).toFixed(2) : 0;
    const poorPercentage =
      total > 0 ? ((poorCount / total) * 100).toFixed(2) : 0;

    return {
      goodCount,
      moderateCount,
      poorCount,
      goodPercentage: parseFloat(goodPercentage),
      moderatePercentage: parseFloat(moderatePercentage),
      poorPercentage: parseFloat(poorPercentage),
    };
  };

  const {
    goodCount,
    moderateCount,
    poorCount,
    goodPercentage,
    moderatePercentage,
    poorPercentage,
  } = evaluateProgress();

  const COLOR_GOOD_HEX = "#28a745";
  const COLOR_MODERATE_HEX = "#ffc107";
  const COLOR_POOR_HEX = "#dc3545";

  const COLOR_GOOD_CLASS = "bg-success";
  const COLOR_MODERATE_CLASS = "bg-warning";
  const COLOR_POOR_CLASS = "bg-danger";

  const renderChart = (percentage, fixedColor) => {
    const chartValue = percentage <= 0 || isNaN(percentage) ? 0 : percentage;

    const activeBarColor = fixedColor;

    const dataLabelColor = chartValue === 0 ? "#adb5bd" : activeBarColor;

    const trackColor = "#e9ecef";

    const seriesData = [chartValue];

    return (
      <Chart
        options={{
          chart: { type: "radialBar" },
          plotOptions: {
            radialBar: {
              hollow: {
                size: "70%",
                background: "#fff",
              },
              track: {
                show: true,
                background: trackColor,
              },
              dataLabels: {
                name: { show: false },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: dataLabelColor,
                  offsetY: 5,
                  formatter: (val) => `${val.toFixed(0)}%`,
                },
              },
            },
          },
          colors: [activeBarColor],
        }}
        series={seriesData}
        type="radialBar"
        height={200}
      />
    );
  };

  return (
    <>
      <div className="row">
        <div className="col-md-4 ">
          <div className="card zis-shadow rounded-0">
            <div className="card-body">
              {renderChart(goodPercentage, COLOR_GOOD_HEX)}
              <ProgressStatus
                status="Good"
                count={goodCount}
                iconClass="fas fa-check-circle"
                colorClass={COLOR_GOOD_CLASS}
              />
              <ProgressBar percentage={goodPercentage} color={COLOR_GOOD_HEX} />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card zis-shadow rounded-0">
            <div className="card-body">
              {renderChart(moderatePercentage, COLOR_MODERATE_HEX)}
              <ProgressStatus
                status="Moderate"
                count={moderateCount}
                iconClass="fas fa-exclamation-triangle"
                colorClass={COLOR_MODERATE_CLASS}
              />
              <ProgressBar
                percentage={moderatePercentage}
                color={COLOR_MODERATE_HEX}
              />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card zis-shadow rounded-0">
            <div className="card-body">
              {renderChart(poorPercentage, COLOR_POOR_HEX)}
              <ProgressStatus
                status="Poor"
                count={poorCount}
                iconClass="fas fa-times-circle"
                colorClass={COLOR_POOR_CLASS}
              />
              <ProgressBar percentage={poorPercentage} color={COLOR_POOR_HEX} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
